/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Button, Flex, Input, message, Select, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { getUserInfo } from '../../reducers/User/actions';

const { Title, Text } = Typography;

interface Organization {
  name: string;
  account_id: string;
}

interface RequestAccessProps {
  organizations: Organization[];
  loadingOrgs: boolean;
  onRequestSubmitted: () => void;
}

const RequestAccess: React.FC<RequestAccessProps> = ({ organizations, loadingOrgs, onRequestSubmitted }) => {
  const userInfo: any = useSelector(getUserInfo);
  const [selectedOrgs, setSelectedOrgs] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitRequest = async () => {
    if (!selectedOrgs) {
      message.warning('Please select an organization.');
      return;
    }
    if (!description.trim()) {
      message.warning('Please provide a reason for requesting access.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/access-request/create', {
        email: userInfo?.name,
        organization_ids: [selectedOrgs],
        description: description.trim(),
      });
      message.success('Access request submitted successfully.');
      setSelectedOrgs(undefined);
      setDescription('');
      onRequestSubmitted();
    } catch (error: any) {
      message.error(typeof error === 'string' ? error : 'Failed to submit access request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <Title level={4} style={{ marginTop: 0 }}>
        Request Access
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Select organizations to request access to
      </Text>

      <Flex gap={12} align="flex-end" wrap="wrap">
        <Flex vertical gap={4} style={{ minWidth: 280 }}>
          <Text strong style={{ fontSize: 13 }}>
            User
          </Text>
          <Input value={userInfo?.name || ''} disabled style={{ height: 40 }} />
        </Flex>

        <Flex vertical gap={4} style={{ flex: 1, minWidth: 320 }}>
          <Text strong style={{ fontSize: 13 }}>
            Organizations <Text type="danger">*</Text>
          </Text>
          <Select
            placeholder="Select organization"
            value={selectedOrgs}
            onChange={setSelectedOrgs}
            loading={loadingOrgs}
            showSearch
            optionFilterProp="label"
            style={{ width: '100%', height: 40 }}
            options={organizations.map((org) => ({
              label: org.name,
              value: org.account_id,
            }))}
          />
        </Flex>

        <Flex vertical gap={4} style={{ flex: 1, minWidth: 280 }}>
          <Text strong style={{ fontSize: 13 }}>
            Reason <Text type="danger">*</Text>
          </Text>
          <Input
            placeholder="Reason for requesting access"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ height: 40 }}
          />
        </Flex>

        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSubmitRequest}
          loading={submitting}
          style={{ height: 40 }}
        >
          Submit Request
        </Button>
      </Flex>
    </div>
  );
};

export default RequestAccess;
